import { Button } from '@dynamika-shared/ui/Button/Button';
import { Input } from 'antd';
import { CloseIcon, SearchIcon } from '@dynamika-shared/ui/icons';
import { QueryStatus } from '@restore/queries/queries.types';
import { debounce } from 'lodash';
import { useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';

type Filters = {
  // In some slices status can be an empty string to represent "All"
  status?: QueryStatus | '';
  patientCode?: string;
  search?: string;
};

type SearchKey = Extract<keyof Filters, 'patientCode' | 'search'>;

type Props<F extends Filters> = {
  filters: F;
  setFilters: (payload: {
    filterQuery: keyof F;
    filterValue: string;
  }) => void;
  searchKey: SearchKey & keyof F;
};

const BUTTONS_FILTERS: { title: string; id?: QueryStatus }[] = [
  { title: 'All' },
  { title: 'Created', id: QueryStatus.CREATED },
  { title: 'Open - Site', id: QueryStatus.OPEN_SITE },
  { title: 'Open - IAG', id: QueryStatus.OPEN_IAG },
  { title: 'Closed', id: QueryStatus.CLOSED },
];

export const QueriesFilters = <F extends Filters>({
  filters,
  setFilters,
  searchKey,
}: Props<F>) => {
  const [searchInputValue, setSearchInputValue] = useState(
    (filters[searchKey] as string | undefined) || ''
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((nextValue: string) => {
        setFilters({ filterQuery: searchKey, filterValue: nextValue });
      }, 500),
    [setFilters, searchKey]
  );

  return (
    <div className='flex flex-wrap justify-between'>
      <div className='flex flex-wrap gap-1 pb-[24px]'>
        {BUTTONS_FILTERS.map(({ title, id }) => (
          <Button
            key={title}
            onClick={() => {
              if (id !== filters.status) {
                // Ensure we always pass a string to setFilters; use empty string for "All"
                setFilters({
                  filterQuery: 'status',
                  filterValue: id ? String(id) : '',
                });
              }
            }}
            className={twMerge(
              'min-w-[7rem] bg-white text-black hover:bg-waterLight py-[10px] px-[20px] ',
              ((filters.status ?? '') === (id ?? '') ) && 'bg-waterLight'
            )}
            type='default'
            skipBasicStyles
          >
            {title}
          </Button>
        ))}
      </div>

      <div className='flex items-center gap-3 pb-[24px]'>
        <Input
          placeholder='Search Subject ID...'
          className='min-w-[400px] max-w-lg rounded-[32px] px-2 py-[10px] text-1424 text-spanishGray'
          suffix={
            (filters[searchKey] as string | undefined)?.length ||
            searchInputValue.length ? (
              <CloseIcon
                className='h-2.5 w-2.5 cursor-pointer font-medium'
                onClick={() => {
                  setSearchInputValue('');
                  debouncedSearch('');
                }}
              />
            ) : (
              <SearchIcon className='h-2 w-2 cursor-pointer' />
            )
          }
          value={searchInputValue}
          onChange={({ target: { value } }) => {
            setSearchInputValue(value);
            debouncedSearch(value);
          }}
        />
      </div>
    </div>
  );
};
